function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import PropTypes from 'prop-types';
import { InteractionManager, StyleSheet, TouchableWithoutFeedback, View, Platform } from 'react-native';
import { stylePropTypes } from '@dkrm/general-libs/theme/styles';
import isEmpty from 'lodash/isEmpty';

/** adopted from react-native-paper TouchableRipple
 * Url: https://github.com/callstack/react-native-paper
 * Version: 2.16.0
 * A wrapper for views that should respond to touches.
 * Provides a material "ink ripple" interaction effect for supported platforms (>= Android Lollipop).
 * On unsupported platforms, it falls back to a highlight effect.
 *
 * ## Usage
 * ```js
 * import React from 'react';
 * import { Text, TouchableRipple } from '@dkrm/ui-kit-basic';
 *
 * const MyComponent = () => (
 *   <TouchableRipple
 *     onPress={() => console.log('Pressed')}
 *   >
 *     <Text>Press me</Text>
 *   </TouchableRipple>
 * );
 *
 * export default MyComponent;
 * ```
 * */
const noop = () => {};
const TouchableRipple = /*#__PURE__*/React.memo(props => {
  const {
    containerStyle,
    background,
    borderless,
    disabled: disabledProp,
    rippleColor,
    underlayColor,
    children,
    theme,
    centered,
    onPress,
    style,
    ...rest
  } = props;
  const addRipple = e => {
    const button = e.currentTarget;
    const computedStyle = window.getComputedStyle(button);
    const dimensions = button.getBoundingClientRect();
    let touchX;
    let touchY;
    if (centered) {
      touchX = dimensions.width / 2;
      touchY = dimensions.height / 2;
    } else {
      const startX = !isEmpty(e.nativeEvent.touches) ? e.nativeEvent.touches[0].pageX : e.nativeEvent.pageX;
      const startY = !isEmpty(e.nativeEvent.touches) ? e.nativeEvent.touches[0].pageY : e.nativeEvent.pageY;
      touchX = startX - dimensions.left;
      touchY = startY - dimensions.top;
      if (typeof window !== 'undefined') {
        touchX -= window.scrollX;
        touchY -= window.scrollY;
      }
    }

    /**
     * Get the size of the button to determine how big the ripple should be
     * If ripple is always centered, we don't need to make it too big
     * Otherwise make it twice as big so clicking on one end spreads ripple to other
     */
    const size = centered ? Math.min(dimensions.width, dimensions.height) * 1.25 : Math.max(dimensions.width, dimensions.height) * 2;

    // Create a container for our ripple effect so we don't need to change the parent's style
    const container = document.createElement('span');
    container.setAttribute('data-ripple', '');
    Object.assign(container.style, {
      position: 'absolute',
      pointerEvents: 'none',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      borderTopLeftRadius: computedStyle.borderTopLeftRadius,
      borderTopRightRadius: computedStyle.borderTopRightRadius,
      borderBottomRightRadius: computedStyle.borderBottomRightRadius,
      borderBottomLeftRadius: computedStyle.borderBottomLeftRadius,
      overflow: centered ? 'visible' : 'hidden'
    });

    // Create span to show the ripple effect
    const ripple = document.createElement('span');
    Object.assign(ripple.style, {
      position: 'absolute',
      pointerEvents: 'none',
      backgroundColor: rippleColor,
      borderRadius: '50%',
      /* Transition configuration */
      transitionProperty: 'transform opacity',
      transitionDuration: `${Math.min(size * 1.5, 350)}ms`,
      transitionTimingFunction: 'linear',
      transformOrigin: 'center',
      /* We'll animate these properties */
      transform: 'translate3d(-50%, -50%, 0) scale3d(0.1, 0.1, 0.1)',
      opacity: '0.5',
      /* Position the ripple where cursor was */
      left: `${touchX}px`,
      top: `${touchY}px`,
      width: `${size}px`,
      height: `${size}px`
    });

    // Finally, append it to DOM
    container.appendChild(ripple);
    button.appendChild(container);

    /**
     * rAF runs in the same frame as the event handler
     * Use double rAF to ensure the transition class is added in next frame
     * This will make sure that the transition animation is triggered
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        Object.assign(ripple.style, {
          transform: 'translate3d(-50%, -50%, 0) scale3d(1, 1, 1)',
          opacity: '1'
        });
      });
    });
  };
  const removeRipple = e => {
    return new Promise(resolve => {
      /**
       * querySelectorAll return NodeList object
       * need to convert NodeList to Array object since Firefox < 50 does not have forEach function for NodeList
       */
      const containers = Array.from(e.currentTarget.querySelectorAll('[data-ripple]'));
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          containers.forEach(container => {
            const ripple = container.firstChild;
            Object.assign(ripple.style, {
              transitionDuration: '250ms',
              opacity: 0
            });

            // Remove the span after the transition
            setTimeout(() => {
              const {
                parentNode
              } = container;
              if (parentNode) {
                parentNode.removeChild(container);
              }
              resolve();
            }, 500);
          });
        });
      });
    });
  };
  const _handlePress = async e => {
    const animationHandle = InteractionManager.createInteractionHandle();
    addRipple(e);
    if (onPress) {
      InteractionManager.runAfterInteractions(() => {
        onPress(e);
      });
    }
    await removeRipple(e);
    InteractionManager.clearInteractionHandle(animationHandle);
  };
  const disabled = disabledProp || !onPress;
  const viewStyle = [style, s.touchable, borderless && s.borderless, containerStyle];
  return /*#__PURE__*/React.createElement(View, _extends({
    onClick: disabled ? undefined : _handlePress,
    accessibilityRole: "button"
  }, rest, {
    pointerEvents: "box-only",
    style: viewStyle
  }), React.Children.only(children));
});
TouchableRipple.displayName = "TouchableRipple";
const s = StyleSheet.create({
  touchable: {
    position: 'relative',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: '150ms background-color'
    })
  },
  borderless: {
    overflow: 'hidden'
  }
});
TouchableRipple.propTypes = process.env.NODE_ENV !== "production" ? {
  ...TouchableWithoutFeedback.propTypes,
  /**
   * Whether to render the ripple outside the view bounds.
   */
  borderless: PropTypes.bool,
  /**
   * Type of background drawabale to display the feedback (Android).
   * https://facebook.github.io/react-native/docs/touchablenativefeedback.html#background
   */
  background: PropTypes.shape(),
  /**
   * Whether to start the ripple at the center (Web).
   */
  centered: PropTypes.bool,
  /**
   * Whether to prevent interaction with the touchable.
   */
  disabled: PropTypes.bool,
  /**
   * Function to execute on press. If not set, will cause the touchable to be disabled.
   */
  onPress: PropTypes.func,
  /**
   * Function to execute on long press.
   */
  onLongPress: PropTypes.func,
  /**
   * Color of the ripple effect (Android >= 5.0 and Web).
   */
  rippleColor: PropTypes.string,
  /**
   * Color of the underlay for the highlight effect (Android < 5.0 and iOS).
   */
  underlayColor: PropTypes.string,
  /**
   * Content of the `TouchableRipple`.
   */
  children: PropTypes.node.isRequired,
  /**
   * Style for View inside TouchableWithoutFeedback
   */
  containerStyle: stylePropTypes,
  /**
   * Style for View containing children, some components pass 'style'
   * props directly
   */
  style: stylePropTypes
} : {};
TouchableRipple.defaultProps = {
  ...TouchableWithoutFeedback.defaultProps,
  borderless: false,
  background: {},
  centered: false,
  disabled: false,
  onPress: noop,
  onLongPress: noop,
  rippleColor: 'rgba(0, 0, 0, .16)',
  underlayColor: '',
  containerStyle: null,
  style: null
};
export default TouchableRipple;